import { type Colors } from '@factorialco/deprecated-design-system'
import { ShiftsShiftFragment } from 'generated/resources/graphql'
import partition from 'lodash/partition'
import uniq from 'lodash/uniq'
import { computed, makeObservable } from 'mobx'
import moment from 'moment'

import Shift, { ShiftPayload } from 'modules/shift_management/store/internal/models/Shift'
import type View from 'modules/shift_management/store/internal/models/View'
import PlannedBreaks from 'modules/time_planning/store/internal/collections/plannedBreaks'

interface BuildFromGraphQLProps {
  shifts: ShiftsShiftFragment[]
  employeeFullName: string
  employeeAvatar: string
  employeeLocationId: number | null
}

class Shifts {
  shifts: Shift[]

  constructor(shifts: Shift[]) {
    this.shifts = shifts

    makeObservable(this, {
      shiftIds: computed,
      employeeIds: computed,
      plannedBreaksCollection: computed,
    })
  }

  /**
   * Transforms an array of objects from a request into an array of Shift
   * objects
   */
  /**
   * @deprecated Before using this method, consider using the `buildFromGraphQL` hook since
   * we are trying to move away from using mobx-rest and use GraphQL instead.
   */
  static buildFromRequest(shifts: ShiftPayload[]): Shifts {
    const shiftObjects = shifts.map((shift) => Shift.buildFromRequest(shift))

    return new Shifts(shiftObjects)
  }

  static buildFromGraphQL({
    shifts,
    employeeFullName,
    employeeAvatar,
    employeeLocationId,
  }: BuildFromGraphQLProps): Shifts {
    return new Shifts(
      shifts.map((shift) => {
        return Shift.buildFromGraphQL({
          shift,
          employeeFullName,
          employeeAvatar,
          employeeLocationId,
        })
      })
    )
  }

  get shiftIds(): number[] {
    return this.shifts.map((s) => s.id)
  }

  get employeeIds(): number[] {
    return uniq(this.shifts.map((s) => s.employeeId))
  }

  get plannedBreaksCollection(): PlannedBreaks {
    return new PlannedBreaks(
      this.shifts.flatMap((shift) => shift.plannedBreaks?.plannedBreaks || [])
    )
  }

  duration(): moment.Duration {
    const initial = moment.duration(0, 'seconds')

    const reducer = (memo: moment.Duration, shift: Shift) => {
      if (memo) memo.add(shift.duration)

      return memo
    }

    return this.shifts.reduce(reducer, initial) || initial
  }

  durationOvernightToNextWeek(): moment.Duration {
    const initial = moment.duration(0, 'seconds')

    const reducer = (memo: moment.Duration, shift: Shift) => {
      if (memo) {
        shift.isOvernightByCalendarDay
          ? memo.add(shift.durationUntilMidnight)
          : memo.add(shift.duration)
      }

      return memo
    }

    return this.shifts.reduce(reducer, initial) || initial
  }

  durationOvernightFromPreviousWeek(): moment.Duration {
    const initial = moment.duration(0, 'seconds')

    const reducer = (memo: moment.Duration, shift: Shift) => {
      if (memo) {
        shift.isOvernightByCalendarDay
          ? memo.add(shift.durationFromMidnight)
          : memo.add(shift.duration)
      }

      return memo
    }

    return this.shifts.reduce(reducer, initial) || initial
  }

  addShifts(shifts: Shift[]): void {
    this.shifts.push(...shifts)
  }

  filterByDate(date: moment.Moment, view: View): Shift[] {
    if (view.isDaily) return this.shifts.filter((shift: Shift) => shift.isHour(date))

    return this.shifts.filter((shift: Shift) => shift.isDate(date))
  }

  filterByNightShiftEndDate(date: moment.Moment): Shift[] {
    return this.shifts.filter((shift: Shift) => shift.isEndNightShift(date))
  }

  filterByRange(firstDay: moment.Moment, lastDay: moment.Moment): Shifts {
    const shifts = this.shifts.filter((shift: Shift) => shift.inRange(firstDay, lastDay))

    return new Shifts(shifts)
  }

  filterByWithinRange(firstDay: moment.Moment, lastDay: moment.Moment): Shifts {
    const shifts = this.shifts.filter(
      (shift: Shift) =>
        shift.inRange(firstDay, lastDay) &&
        !shift.isBeginNightShift(lastDay) &&
        !shift.isEndNightShift(firstDay)
    )

    return new Shifts(shifts)
  }

  filterByNotBackupAndWithinRange(firstDay: moment.Moment, lastDay: moment.Moment): Shifts {
    const shifts = this.shifts.filter(
      (shift: Shift) =>
        shift.inRange(firstDay, lastDay) &&
        !shift.isBeginNightShift(lastDay) &&
        !shift.isEndNightShift(firstDay) &&
        !shift.isBackup
    )

    return new Shifts(shifts)
  }

  filterByOvernightToNextRange(lastDay: moment.Moment): Shifts {
    const overnightNextRange = this.shifts.filter(
      (shift: Shift) => shift.isBeginNightShift(lastDay) && !shift.isBackup
    )

    return new Shifts(overnightNextRange)
  }

  filterByOvernightFromPreviousRange(firstDay: moment.Moment): Shifts {
    const overnightPreviousRange = this.shifts.filter(
      (shift: Shift) =>
        shift.isOvernightByCalendarDay && shift.isEndNightShift(firstDay) && !shift.isBackup
    )

    return new Shifts(overnightPreviousRange)
  }

  filterByEmployeeIds(employeeIds: number[]): Shift[] {
    const set = new Set(employeeIds)
    return this.shifts.filter((shift: Shift) => set.has(shift.employeeId))
  }

  filterByNonEmployeeIds(employeeIds: number[]): Shift[] {
    const set = new Set(employeeIds)
    return this.shifts.filter((shift: Shift) => !set.has(shift.employeeId))
  }

  filterByDraft(): Shift[] {
    return this.shifts.filter((shift: Shift) => shift.isDraft)
  }

  filterByPublished(): Shift[] {
    return this.shifts.filter((shift: Shift) => shift.isPublished)
  }

  filterByDefaultShift(defaultShiftId: number): Shift[] {
    return this.shifts.filter((shift: Shift) => shift.defaultShiftId === defaultShiftId)
  }

  filterByUnassignedShift(unassignedShiftId: number): Shift[] {
    return this.shifts.filter((shift: Shift) => shift.unassignedShiftId === unassignedShiftId)
  }

  destroyShift(id: number): void {
    const index = this.shifts.findIndex((shift) => shift.id === id)
    if (index >= 0) this.shifts.splice(index, 1)
  }

  destroyShifts(ids: number[]): Shifts {
    const idsSet = new Set(ids)

    const [shiftsToDestroy, shiftsToAdd] = partition(this.shifts, (s: Shift) => idsSet.has(s.id))

    if (shiftsToDestroy.length === ids.length) {
      return new Shifts(shiftsToAdd)
    } else return this
  }

  includes(shift: Shift) {
    return this.shiftIds.includes(shift.id)
  }

  during(date: moment.Moment): Shift[] {
    return this.shifts.filter((shift: Shift) => shift.isDuring(date))
  }

  updateShiftsColor(ids: number[], color: Colors): Shifts {
    const idsSet = new Set(ids)
    const [shiftsToColor, shifts] = partition(this.shifts, (s: Shift) => idsSet.has(s.id))
    const coloredShifts = shiftsToColor.map((shift) => {
      shift.color = color
      return shift
    })

    if (!!coloredShifts) {
      return new Shifts([...coloredShifts, ...shifts])
    } else return this
  }
}

export default Shifts
